import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle } from "lucide-react";
import {
  StatCard, Card, Loading, ErrorState, RangePicker, TrendChart,
  TableShell, Th, Pill, EmptyState,
} from "./AdminUI";

export default function MarketplaceTab() {
  const [days, setDays] = useState(7);
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setError(null); setD(null);
    api.get("/admin/marketplace", { params: { days } })
      .then(r => setD(r.data)).catch(e => setError(e.response?.data?.detail || e.message));
  }, [days]);
  useEffect(() => { load(); }, [load]);

  if (error) return <ErrorState error={error} onRetry={load} />;

  return (
    <div className="space-y-5" data-testid="admin-marketplace-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-[#222]/55 max-w-xl">
          Is the Requests marketplace actually working? Response data comes from the existing message
          threads attached to each Request — no new tracking was added.
        </p>
        <RangePicker value={days} onChange={setDays} options={[7, 30, 90]} testid="marketplace-range" />
      </div>

      {!d ? <Loading /> : (
        <>
          {d.response_health.requests_without_response > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 flex items-start gap-3"
              data-testid="marketplace-problem">
              <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm font-medium">
                {d.response_health.requests_without_response} Request
                {d.response_health.requests_without_response === 1 ? " has" : "s have"} received no
                Professional response in the last {d.range_days} days.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <StatCard testid="mk-created" label={`Created (${d.range_days}d)`} value={d.totals.created_in_range}
              sub={`${d.totals.all_time} all time`} />
            <StatCard testid="mk-open" label="Open now" value={d.totals.open} />
            <StatCard testid="mk-filled" label="Filled" value={d.totals.filled}
              sub={`${d.totals.closed} closed · ${d.totals.expired} expired`} />
            <StatCard testid="mk-rate" label="Response rate"
              value={d.response_health.response_rate_pct == null ? "—" : `${d.response_health.response_rate_pct}%`}
              sub={`${d.response_health.requests_with_response} of ${d.totals.created_in_range}`}
              tone={d.response_health.response_rate_pct != null && d.response_health.response_rate_pct < 50 ? "alert" : undefined} />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card title="Response quality" testid="mk-quality">
              <div className="space-y-2 text-sm">
                <Row label="Avg responding Pros per answered Request"
                  value={d.response_health.avg_responding_pros ?? "No responses yet"} />
                <Row label="Median hours to first response"
                  value={d.response_health.median_hours_to_first_response == null ? "No responses yet"
                    : `${d.response_health.median_hours_to_first_response}h`} />
                <Row label="Answered" value={d.response_health.requests_with_response} />
                <Row label="Unanswered" value={d.response_health.requests_without_response} />
              </div>
              <p className="text-[11px] text-[#222]/45 mt-3">Source: {d.response_health.source}</p>
            </Card>
            <Card title="Most active categories" testid="mk-categories">
              {d.top_categories.length === 0 ? (
                <p className="text-sm text-[#222]/45 py-6 text-center">No Requests in this period.</p>
              ) : (
                <div className="space-y-1.5">
                  {d.top_categories.map(c => (
                    <div key={c.name} className="flex items-center justify-between text-sm border-b border-black/5 pb-1.5 last:border-0">
                      <span className="truncate">{c.name}</span>
                      <Pill tone="neutral">{c.value}</Pill>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <TrendChart testid="mk-trend" title="Requests created over time"
            subtitle="Demand volume. Source: requests.created_at"
            data={d.created_trend} type="bar" />

          <div>
            <h3 className="text-sm font-bold mb-3" style={{ fontFamily: "Outfit" }}>
              Requests with no Professional response
            </h3>
            {d.unanswered.length === 0 ? (
              <EmptyState title="Every Request got a response"
                body={`No unanswered Requests in the last ${d.range_days} days.`}
                testid="mk-unanswered-empty" />
            ) : (
              <TableShell testid="mk-unanswered-table" maxHeight="50vh">
                <thead>
                  <tr><Th>Request</Th><Th>Category</Th><Th>Status</Th><Th>Posted</Th></tr>
                </thead>
                <tbody>
                  {d.unanswered.map(r => (
                    <tr key={r.id} className="border-t border-black/5" data-testid={`mk-unanswered-${r.id}`}>
                      <td className="px-4 py-3 max-w-[320px] truncate font-medium">{r.title || "—"}</td>
                      <td className="px-4 py-3 text-xs">{r.category || "—"}</td>
                      <td className="px-4 py-3"><Pill tone={r.status === "open" ? "green" : "neutral"}>{r.status}</Pill></td>
                      <td className="px-4 py-3 text-xs text-[#222]/50 whitespace-nowrap">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TableShell>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-black/5 pb-1.5 last:border-0">
      <span className="text-[#222]/60 text-xs">{label}</span>
      <span className="font-semibold text-sm">{value}</span>
    </div>
  );
}
